import type { StatusTier } from "@/components/ui/badge"

import type { DryRunResult } from "../health/types"

export type EmailStatus =
  | "pending"
  | "sent"
  | "delivered"
  | "bounced"
  | "complained"
  | "failed"
  | "suppressed"

export interface EmailMessageSummary {
  id: number
  to: string
  subject: string | null
  mailer: string
  status: EmailStatus
  route: string
  cause: string | null
  created_at: string
  last_event_at: string | null
}

export const STATUS_LABEL: Record<EmailStatus, string> = {
  pending: "Pending",
  sent: "Sent",
  delivered: "Delivered",
  bounced: "Bounced",
  complained: "Complained",
  failed: "Failed",
  suppressed: "Suppressed",
}

export const STATUS_COLOR: Record<EmailStatus, StatusTier> = {
  pending: "neutral",
  sent: "remote",
  delivered: "confirmed",
  bounced: "caution",
  complained: "caution",
  failed: "caution",
  suppressed: "caution",
}

export const ROUTE_COLOR: Record<string, StatusTier> = {
  live: "remote",
  sandbox: "caution",
}

/**
 * MessageRoutingDryRun's answer: the message-level outcome, with the
 * per-recipient single-address results it was derived from.
 */
export interface MessagePreview {
  outcome: DryRunResult["outcome"]
  explanation: string
  recipients: DryRunResult[]
}

/**
 * A provider event as it arrived, kept alongside what the row became. The
 * payload itself is deliberately not sent to the client — it carries the
 * recipient address and the provider's description of a dead address, and
 * neither screen needs the whole blob to be readable.
 */
export interface WebhookEvent {
  id: number
  record_type: string
  outcome: WebhookOutcome
  outcome_detail: string | null
  received_at: string
  provider_message_id: string | null
  description: string | null
  payload_keys: string[]
}

export type WebhookOutcome =
  | "received"
  | "matched"
  | "unmatched"
  | "ignored"
  | "failed"

export const OUTCOME_LABEL: Record<WebhookOutcome, string> = {
  received: "Received",
  matched: "Matched",
  unmatched: "Unmatched",
  ignored: "Ignored",
  failed: "Failed",
}

export const OUTCOME_COLOR: Record<WebhookOutcome, StatusTier> = {
  received: "neutral",
  matched: "confirmed",
  unmatched: "caution",
  ignored: "system",
  failed: "caution",
}
